import React, { useEffect, useState } from 'react';
import * as api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/rbac';
import { pickErrorMessage } from '../lib/apiHelpers';
import { toastSuccess } from '../lib/toast';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';

export default function Settings() {
  const { admin, hasRole, reloadSession } = useAuth();
  const isBrand = hasRole(ROLES.BRAND_ADMIN);
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [profileBusy, setProfileBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.fetchSettings();
        if (!cancelled) {
          setSettings(data);
          setName(data.name || '');
          setBrandName(data.brandName || '');
        }
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function saveProfile(e) {
    e.preventDefault();
    setProfileBusy(true);
    setError('');
    try {
      const body = { name: name.trim() };
      if (isBrand) body.brandName = brandName.trim();
      await api.updateSettings(body);
      toastSuccess('Profile updated.');
      if (reloadSession) await reloadSession();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setProfileBusy(false);
    }
  }

  async function savePassword(e) {
    e.preventDefault();
    setPasswordBusy(true);
    setError('');
    try {
      await api.changePassword({ currentPassword, newPassword });
      toastSuccess('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setPasswordBusy(false);
    }
  }

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status" /></div>;
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="Account and application preferences" />
      <ErrorBanner message={error} />
      <div className="row g-3">
        <div className="col-lg-6">
          <Card>
            <CardBody>
              <h2 className="h6 mb-3">Profile</h2>
              <form onSubmit={saveProfile}>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input className="form-control" value={settings?.email || admin?.email || ''} disabled />
                </div>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Display name</label>
                  <input id="name" className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                {isBrand ? (
                  <div className="mb-3">
                    <label htmlFor="brandName" className="form-label">Brand name</label>
                    <input id="brandName" className="form-control" value={brandName} onChange={(e) => setBrandName(e.target.value)} required />
                  </div>
                ) : null}
                {settings?.couponQuota !== undefined ? (
                  <p className="small text-secondary">
                    Coupon quota: {settings.couponQuota}
                    {settings.quotaRemaining !== undefined ? ` (${settings.quotaRemaining} remaining)` : ''}
                  </p>
                ) : null}
                <button type="submit" className="btn btn-primary" disabled={profileBusy}>
                  {profileBusy ? 'Saving…' : 'Save profile'}
                </button>
              </form>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-6">
          <Card>
            <CardBody>
              <h2 className="h6 mb-3">Change password</h2>
              <form onSubmit={savePassword}>
                <div className="mb-3">
                  <label htmlFor="current" className="form-label">Current password</label>
                  <input id="current" type="password" className="form-control" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="newpass" className="form-label">New password</label>
                  <input id="newpass" type="password" className="form-control" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
                </div>
                <button type="submit" className="btn btn-outline-primary" disabled={passwordBusy}>
                  {passwordBusy ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
